import { useUserConfigurations } from "@via/api/configuration";
import { useAgentStatus } from "@via/pages/account/profile/run-environments/agent.api.tsx";
import { AgentCard } from "@via/pages/account/profile/run-environments/run-environment-form/cards/agent-card.tsx";
import { type ProfileClusterForClient } from "common";
import { type JSX } from "react";

/**
 * Which rows open in place. Agent environments do; ssh ones keep the edit drawer, because a
 * Foundry Connect run environment also carries connection, execution, container, directories,
 * variables, access and cloud credentials, and none of that belongs in a row.
 *
 * ⚠️ Defaults to false. `transport` is `NOT NULL DEFAULT 'ssh'` in the schema, but a row that
 * reaches this client without the field reads as undefined, and offering an agent panel on an
 * ssh environment is worse than offering nothing.
 */
export const isAgentRowExpandable = (
  environment: Partial<ProfileClusterForClient>
): boolean => "agent" === environment.transport;

/**
 * The body of an expanded agent row: the agent's own panel, beside the environment it belongs
 * to, rather than three clicks away inside the edit drawer. This is the surface science-ui
 * calls the environment card.
 *
 * Phase B adds a Config tab here and Phase C adds Images. There is deliberately no tab strip
 * yet: one tab's worth of content does not need one.
 */
export function AgentRowExpandedContent({
  environment,
}: {
  environment: ProfileClusterForClient;
}): JSX.Element {
  // ⚠️ Polled here rather than by the card, mirroring what the form already does. The card
  // takes status as a prop precisely so one subscription feeds it, and a second subscriber
  // inside the card would let the row and the card disagree while a poll is in flight.
  const { data: status, isError } = useAgentStatus(environment.id);
  // The apps line of the capability warning follows the instance switch, which the form reads for the same
  // card; no form sits above this row, so it reads it here (agent-4-2).
  const { data: userConfigurations } = useUserConfigurations();

  return (
    <div className="p-4" data-testid="agent-row-body">
      <AgentCard
        environmentId={environment.id}
        status={status}
        statusError={isError}
        hpcAppsEnabled={Boolean(userConfigurations?.HPC_APPS_ENABLED)}
      />
    </div>
  );
}
